/**
 * TapTempo — convert a sequence of tap timestamps into a BPM value.
 *
 * The caller feeds timestamps via `tap(nowMs)` (in milliseconds, as
 * from `performance.now()` or `Date.now()`). The class tracks a
 * sliding window of the most recent taps and returns the BPM
 * averaged across the intervals between them.
 *
 * Two behaviors keep the UX feeling right:
 *   1. Reset window when the gap between consecutive taps exceeds
 *      `resetAfterMs` (default 2000 ms). "I tapped three times, then
 *      walked away" should not feed ghost intervals into the next
 *      session.
 *   2. Clamp the result to `[minBpm, maxBpm]` — a missed tap or an
 *      unreasonably fast / slow input shouldn't yank the transport to
 *      an unusable tempo.
 *
 * Pure class — no DOM, no timers, no AudioContext. The UI layer owns
 * the button and the visual feedback; TapTempo just does the math.
 */

export interface TapTempoOptions {
  /** Window size — BPM is averaged across this many most-recent intervals. */
  readonly maxTaps?: number;
  /** Gap (ms) past which the window resets on the next tap. */
  readonly resetAfterMs?: number;
  readonly minBpm?: number;
  readonly maxBpm?: number;
}

export interface TapResult {
  /** Number of taps currently held in the window (1+). */
  readonly tapCount: number;
  /** Computed BPM — null until the 2nd tap arrives (need >= 1 interval). */
  readonly bpm: number | null;
}

export class TapTempo {
  private readonly maxTaps: number;
  private readonly resetAfterMs: number;
  private readonly minBpm: number;
  private readonly maxBpm: number;
  private taps: number[] = [];

  constructor(opts: TapTempoOptions = {}) {
    // 4 taps by default — matches most hardware metronomes and gives
    // a stable average after the first downbeat "ready" tap.
    this.maxTaps = Math.max(2, opts.maxTaps ?? 4);
    this.resetAfterMs = Math.max(100, opts.resetAfterMs ?? 2000);
    this.minBpm = Math.max(1, opts.minBpm ?? 20);
    this.maxBpm = Math.max(this.minBpm, opts.maxBpm ?? 300);
  }

  /**
   * Register a tap. `nowMs` should be monotonically increasing; the
   * caller can use `performance.now()` (recommended) or `Date.now()`
   * — the math only cares about deltas.
   *
   * Returns the updated state: tap count in the window plus the BPM
   * (null on the very first tap, since averaging needs at least one
   * interval).
   */
  tap(nowMs: number): TapResult {
    const last = this.taps[this.taps.length - 1];
    if (last !== undefined && nowMs - last > this.resetAfterMs) {
      // Long gap — start over. Keep only the new tap as the anchor.
      this.taps = [nowMs];
      return { tapCount: 1, bpm: null };
    }
    this.taps.push(nowMs);
    if (this.taps.length > this.maxTaps) {
      // Oldest tap ages out so the average keeps reflecting "what the
      // user is tapping right now", not "what they tapped 20 bars ago".
      this.taps.shift();
    }
    if (this.taps.length < 2) return { tapCount: 1, bpm: null };
    // Average the intervals rather than first→last span / (n-1) — they're
    // equivalent, but iterating pair-by-pair is easier to reason about
    // when we later add outlier rejection.
    let totalInterval = 0;
    for (let i = 1; i < this.taps.length; i++) {
      totalInterval += this.taps[i]! - this.taps[i - 1]!;
    }
    const avgMs = totalInterval / (this.taps.length - 1);
    const rawBpm = 60000 / avgMs;
    const bpm = Math.max(this.minBpm, Math.min(this.maxBpm, rawBpm));
    return { tapCount: this.taps.length, bpm };
  }

  /** Clear the tap window. Useful when a preset load resets BPM. */
  reset(): void {
    this.taps = [];
  }

  /** Number of taps currently held. Handy for UI indicators. */
  get count(): number {
    return this.taps.length;
  }
}
